import React from "react";
import { Button, Input, Pagination } from "@wac-ui-dashboard/wac_component_library";
import Select from "react-select";
import EmptyData from "../../Global/EmptyData";
import useSubscriptionReport, {
  LEDGER_TYPES,
  REPORT_TYPES,
} from "./useSubscriptionReport";

const money = (value) =>
  value === undefined || value === null
    ? "-"
    : `KD ${Number(value).toFixed(3)}`;

/**
 * The ledger stores debits negative so that a subscription's rows sum to its balance. That
 * sign is an accounting device and reads as an error in front of anyone else, so the table
 * names the movement in full and shows the quantity as a magnitude.
 */
const LEDGER_TYPE_LABELS = {
  debit: "Redeemed",
  credit_void: "Credited back (void)",
  credit_manual: "Credited back (manual)",
  release_unpaid: "Released (unpaid)",
  reclaim_released: "Re-taken (paid after release)",
  cancel_forfeit: "Written off (cancelled)",
};

const magnitude = (value) => Math.abs(Number(value ?? 0));

const Card = ({ label, value, hint }) => (
  <div className="col-3 pro-mb-4">
    <div className="pro-p-4 pro-bg-light rounded-3 pro-h-100">
      <span className="pro-font-sm">{label}</span>
      <p className="pro-mb-0 pro-fw-bolder pro-font-l">{value}</p>
      {hint && <span className="pro-font-xs">{hint}</span>}
    </div>
  </div>
);

const SubscriptionReport = () => {
  const {
    page,
    email,
    plans,
    outlets,
    filters,
    summary,
    setPage,
    setEmail,
    showPlans,
    showLedger,
    ledgerType,
    reportType,
    showSummary,
    showOutlets,
    redemptionList,
    isLedgerFetching,
    isSummaryLoading,
    handleFilterChange,
    handleReportTypeChange,
    handleLedgerTypeChange,
    handleExport,
  } = useSubscriptionReport();

  return (
    <div className="pro-pt-3 pro-pb-6">
      <div className="row pro-mb-4">
        <div className="col-3">
          <label htmlFor="report_type" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Report
          </label>
          <Select
            id="report_type"
            options={REPORT_TYPES}
            getOptionLabel={(option) => `${option.name}`}
            getOptionValue={(option) => option.id}
            className={`pro-input lg pro-min-full multi-select`}
            classNamePrefix="pro-input"
            value={REPORT_TYPES.filter((type) => type.id === reportType)}
            onChange={(option) => handleReportTypeChange(option?.id)}
          />
        </div>
        <div className="col-2">
          <label htmlFor="start_date" className="pro-font-sm pro-mb-1 pro-fw-medium">
            From
          </label>
          <input
            type="date"
            id="start_date"
            className="pro-input lg"
            value={filters.start_date}
            onChange={(e) => handleFilterChange("start_date", e.target.value)}
          />
        </div>
        <div className="col-2">
          <label htmlFor="end_date" className="pro-font-sm pro-mb-1 pro-fw-medium">
            To
          </label>
          <input
            type="date"
            id="end_date"
            className="pro-input lg"
            value={filters.end_date}
            onChange={(e) => handleFilterChange("end_date", e.target.value)}
          />
        </div>
        {/* Summary and outlet have no export endpoint, so the address field would be dead
            weight there - it appears only alongside a button that can use it. */}
        {(showPlans || showLedger) && (
          <div className="col-3">
            <Input
              label={"Email the report to"}
              type="email"
              id="report_email"
              name="report_email"
              className="pro-input lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        )}
        {/* The queued export is the plan breakdown only, so it is offered only where that
            table is on screen - the ledger has its own button. */}
        {showPlans && (
          <div className="col-2 pro-d-flex pro-items-end pro-pb-4">
            <Button
              className={"pro-btn-outline lg"}
              onClick={() => handleExport("plans")}
            >
              Export
            </Button>
          </div>
        )}
      </div>

      {showSummary && isSummaryLoading && (
        <p className="pro-mb-4">Loading…</p>
      )}
      {showSummary && !isSummaryLoading && (
        <div className="row">
          {/* Sales: dated by activation. */}
          {/* Auto-renew counts subscriptions, so it belongs against a subscription count -
              under "Beverages sold" it read as though six beverages renew themselves. */}
          <Card
            label="Subscriptions sold"
            value={summary?.total_subscriptions ?? 0}
            hint={`Dated by activation · ${
              summary?.auto_pay_enabled ?? 0
            } on auto-renew`}
          />
          <Card label="Revenue" value={money(summary?.total_revenue)} />
          <Card
            label="Active"
            value={summary?.active_subscriptions ?? 0}
            hint={`${summary?.expired_subscriptions ?? 0} expired · ${
              summary?.cancelled_subscriptions ?? 0
            } cancelled`}
          />
          <Card
            label="Outstanding liability"
            value={money(summary?.outstanding_value)}
            hint={`${summary?.beverages_remaining ?? 0} beverages unredeemed${
              summary?.zero_revenue_subscriptions
                ? ` · ${summary.zero_revenue_subscriptions} sold at no charge`
                : ""
            }`}
          />
          {/* Consumption: dated by the ledger, which is a different question. */}
          <Card
            label="Beverages redeemed"
            value={summary?.beverages_redeemed ?? 0}
            hint="Dated by when the beverage moved"
          />
          <Card
            label="Discount given away"
            value={money(summary?.gross_discount_value_redeemed)}
          />
          <Card
            label="Orders with a redemption"
            value={summary?.orders_with_redemption ?? 0}
          />
          <Card label="Beverages sold" value={summary?.beverages_sold ?? 0} />
          <div className="col-12">
            <p className="pro-font-xs pro-mb-0">
              Outstanding liability values every unredeemed beverage at the price frozen on
              its own subscription, so it is not a share of revenue and can read higher than
              it — a plan given away carries beverages to serve without having brought
              anything in.
            </p>
          </div>
        </div>
      )}

      {showPlans && (
        <>
          <h6 className="pro-ttl pro-mb-1 pro-mt-4">By plan</h6>
          <p className="pro-font-xs pro-mb-2">
            The subscriptions activated in this window, counted to date — redeemed and
            remaining are their lifetime position rather than movement inside the window, so
            these will not add up to “Beverages redeemed” above.
          </p>
        </>
      )}
      {showPlans && plans?.length === 0 && <EmptyData />}
      {showPlans && plans?.length > 0 && (
        <div className="pro-table-wrapper pro-mb-5">
          <table className="pro-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Sold</th>
                <th>Revenue</th>
                <th>Beverages sold</th>
                <th>Redeemed to date</th>
                <th>Remaining</th>
                <th>Redemption rate to date</th>
                <th>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((row) => (
                <tr key={row?.plan_id}>
                  <td>{row?.plan_name}</td>
                  <td>{row?.subscriptions_sold}</td>
                  <td>{money(row?.revenue)}</td>
                  <td>{row?.beverages_sold}</td>
                  <td>{row?.beverages_redeemed_to_date}</td>
                  <td>{row?.beverages_remaining}</td>
                  <td>{row?.redemption_rate_to_date}%</td>
                  <td>{money(row?.outstanding_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showOutlets && (
        <>
          <h6 className="pro-ttl pro-mb-1 pro-mt-4">By outlet</h6>
          <p className="pro-font-xs pro-mb-2">
            Read from the ledger — where a plan was bought says nothing about
            where the coffee was served.
          </p>
        </>
      )}
      {showOutlets && outlets?.length === 0 && <EmptyData />}
      {showOutlets && outlets?.length > 0 && (
        <div className="pro-table-wrapper pro-mb-5">
          <table className="pro-table">
            <thead>
              <tr>
                <th>Outlet</th>
                <th>Beverages redeemed</th>
                <th>Discount given</th>
                <th>Orders</th>
              </tr>
            </thead>
            <tbody>
              {outlets.map((row) => (
                <tr key={row?.customer_key}>
                  {/* The key only where the product service could not name the outlet. */}
                  <td>{row?.outlet_name || row?.customer_key}</td>
                  <td>{row?.beverages_redeemed}</td>
                  <td>{money(row?.gross_discount_value)}</td>
                  <td>{row?.orders_with_redemption}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showLedger && (
        <div className="pro-d-flex pro-justify-between pro-items-end pro-mb-2 pro-mt-4">
          <div>
            <h6 className="pro-ttl pro-mb-1">Redemption ledger</h6>
            <p className="pro-font-xs pro-mb-0">
              Redemptions only unless another movement is picked. Quantities read as
              magnitudes, with the direction named in the Type column.
            </p>
          </div>
          <div className="pro-d-flex pro-gap-3 pro-items-end">
            <div style={{ minWidth: "220px" }}>
              <label
                htmlFor="ledger_type"
                className="pro-font-sm pro-mb-1 pro-fw-medium"
              >
                Movement
              </label>
              <Select
                id="ledger_type"
                options={LEDGER_TYPES}
                getOptionLabel={(option) => `${option.name}`}
                getOptionValue={(option) => option.id}
                className={`pro-input lg pro-min-full multi-select`}
                classNamePrefix="pro-input"
                value={LEDGER_TYPES.filter((type) => type.id === ledgerType)}
                onChange={(option) => handleLedgerTypeChange(option?.id)}
              />
            </div>
            <Button
              className={"pro-btn-outline sm"}
              onClick={() => handleExport("redemptions")}
            >
              Export ledger
            </Button>
          </div>
        </div>
      )}
      {showLedger && redemptionList?.result?.length > 0 && (
        <>
          <div className="pro-table-wrapper">
            <table className="pro-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Discount</th>
                  <th>Outlet</th>
                  <th>Order</th>
                </tr>
              </thead>
              <tbody className={isLedgerFetching ? "pro-opacity-50" : ""}>
                {redemptionList.result.map((row) => (
                  <tr key={row?.ledger_id}>
                    <td>{row?.redeemed_on}</td>
                    <td>{LEDGER_TYPE_LABELS[row?.type] ?? row?.type}</td>
                    <td>{row?.item_name}</td>
                    <td>{magnitude(row?.quantity)}</td>
                    <td>{money(magnitude(row?.gross_discount_value))}</td>
                    <td>{row?.outlet_name || row?.customer_key}</td>
                    <td>{row?.order_id ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPageCount={Math.ceil((redemptionList?.total_count ?? 0) / 10)}
            onPageChange={setPage}
            center
          />
        </>
      )}
      {showLedger && !redemptionList?.result?.length && <EmptyData />}
    </div>
  );
};

export default SubscriptionReport;
